import omit from 'lodash/omit'
import * as z from 'zod'
import {
  accountNumberRequiredSchema,
  bikRequiredSchema,
  corrAccountNumberRequiredSchema,
  dateSchema,
  innRequiredSchema,
  kbkRequiredSchema,
  kppRequiredSchema,
  oktmoRequiredSchema,
  priceRequiredScheme,
  stringRequiredScheme,
  uinRequiredSchema,
  vinRequiredSchema,
} from '~/shared/lib/schemas'
import { sanitizePriceNumber } from '~/shared/lib/utils'

export enum PenaltySource {
  esb = 'esb',
  manual = 'manual',
}

/*
 * Схема ручного создания штрафа оператором.
 *
 * Номер постановления не запрашивается: он совпадает с УИН и заполняется на бэкенде.
 * VIN и водитель обязательны — без них штраф не дойдёт до начисления водителю.
 * */
/*
 * Служебные поля формы: хранят выбранный вариант автокомплита.
 * В API не уходят — из них заполняются реальные поля штрафа.
 * */
const autocompleteOptionSchema = z
  .object({ id: z.string(), label: z.string() })
  .passthrough()
  .nullable()
  .optional()

export const penaltyCreateSchema = z.object({
  car: autocompleteOptionSchema,
  driver: autocompleteOptionSchema,
  company: autocompleteOptionSchema,

  paymentUin: uinRequiredSchema,
  protocolDate: dateSchema,
  penaltyDate: dateSchema,
  penaltyTime: z
    .string()
    .trim()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Ожидается время в формате ЧЧ:ММ')
    .optional()
    .or(z.literal('')),
  penaltyLocation: z.string().optional(),

  penaltyLawArticleNumber: stringRequiredScheme,
  penaltyLawArticleDescription: stringRequiredScheme,

  odpsCode: z.string().optional(),
  odpsName: z.string().optional(),
  odpsAddr: z.string().optional(),

  /*
   * Автомобиль, водитель и организация заполняются выбором из справочника,
   * поэтому в сообщениях говорим про выбор, а не про заполнение поля.
   * */
  carVin: z
    .string()
    .min(1, 'Выберите автомобиль из справочника')
    .pipe(vinRequiredSchema),
  carPlateNumber: z.string().min(1, 'Выберите автомобиль из справочника'),
  carSts: z.string().optional(),
  carBrandTitle: z.string().optional(),
  carModelTitle: z.string().optional(),
  carExtId: z.string().optional(),

  tmsDriver: z.string().uuid('Выберите водителя'),
  tmsDriverName: z.string().min(1, 'Выберите водителя из справочника'),
  tmsSubdivision: z.string().optional(),
  tmsSubdivisionName: z.string().optional(),

  paymentOrgInn: z
    .string()
    .min(1, 'Выберите организацию')
    .pipe(innRequiredSchema),
  paymentType: stringRequiredScheme,
  /*
   * Поля сумм и скидки приходят из NumericInput форматированной строкой
   * вида «500 руб» и «50 %», поэтому перед проверкой их нужно очистить.
   * */
  paymentAmount: priceRequiredScheme,
  paymentAmountTotal: priceRequiredScheme,
  paymentDiscountSize: z
    .union([z.string(), z.number()])
    .optional()
    .transform((value) =>
      value === undefined || value === ''
        ? undefined
        : sanitizePriceNumber(value),
    )
    .refine(
      (value) => value === undefined || (value >= 0 && value <= 100),
      'Скидка должна быть от 0 до 100%',
    ),
  paymentDiscountDate: dateSchema.optional(),
  paymentUntilDate: dateSchema.optional(),

  recipient: stringRequiredScheme,
  recipientInn: innRequiredSchema,
  recipientKpp: kppRequiredSchema,
  recipientBankAccountNumber: accountNumberRequiredSchema,
  recipientBankCorrAccountNumber: corrAccountNumberRequiredSchema,
  recipientBankName: stringRequiredScheme,
  recipientBankBik: bikRequiredSchema,
  recipientBankPurpose: stringRequiredScheme,
  recipientBankUsername: stringRequiredScheme,
  recipientBankKbk: kbkRequiredSchema,
  recipientBankOktmo: oktmoRequiredSchema,

  comment: z.string().optional(),
})

export type PenaltyCreateFormValues = z.infer<typeof penaltyCreateSchema>

/*
 * Дата и время нарушения вводятся раздельно, а в API уходят одним полем:
 * время указано на постановлении и нужно в карточке штрафа.
 * */
export type PenaltyCreateValues = Omit<
  PenaltyCreateFormValues,
  | 'penaltyDate'
  | 'penaltyTime'
  | 'car'
  | 'driver'
  | 'company'
  | 'paymentDiscountSize'
> & {
  penaltyDatetime: string
  /* JSON:API-схема штрафа описывает размер скидки строкой. */
  paymentDiscountSize?: string
}

export const toPenaltyCreateValues = (
  values: PenaltyCreateFormValues,
): PenaltyCreateValues => ({
  ...omit(values, [
    'penaltyDate',
    'penaltyTime',
    'car',
    'driver',
    'company',
    'paymentDiscountSize',
  ]),
  /*
   * Отправляется время, как оно указано в постановлении, то есть местное:
   * в UTC его переводит бэкенд, который знает часовой пояс пользователя.
   * */
  penaltyDatetime: `${values.penaltyDate} ${values.penaltyTime || '00:00'}:00`,
  ...(values.paymentDiscountSize === undefined
    ? {}
    : { paymentDiscountSize: String(values.paymentDiscountSize) }),
})

/*
 * Реквизиты получателя, подставляемые по коду подразделения ГАИ.
 * */
export type PenaltyRecipientByOdps = {
  odpsCode: string | null
  odpsName: string | null
  odpsAddr: string | null
  recipient: string | null
  recipientInn: string | null
  recipientKpp: string | null
  recipientBankAccountNumber: string | null
  recipientBankCorrAccountNumber: string | null
  recipientBankName: string | null
  recipientBankBik: string | null
  recipientBankUsername: string | null
  recipientBankKbk: string | null
  recipientBankOktmo: string | null
}
